<template>
  <div class="text-center mb-5" v-if="step == 1">
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 36.666C29.2 36.666 36.6666 29.1994 36.6666 19.9994C36.6666 10.7994 29.2 3.33269 20 3.33269C10.8 3.33268 3.3333 10.7994 3.33329 19.9993C3.33329 29.1993 10.8 36.666 20 36.666ZM20 11.666C20.9166 11.666 21.6666 12.416 21.6666 13.3327L21.6666 19.9994C21.6666 20.916 20.9166 21.666 20 21.666C19.0833 21.666 18.3333 20.916 18.3333 19.9994L18.3333 13.3327C18.3333 12.416 19.0833 11.666 20 11.666ZM18.3333 24.9994L21.6666 24.9994L21.6666 28.3327L18.3333 28.3327L18.3333 24.9994Z" fill="#FFCE31"/>
    </svg>
    <div class="title mt-1 mb-2">
      {{$t('Смена адреса страницы')}}
    </div>
    <div class="d-flex justify-center text-center mb-4">
      <div class="copy-description">
        {{$t('Контент и оформление останутся без изменений, поменяется только ссылка')}}
      </div>
    </div>

    <div class="d-flex flex-column ga-2">
      <v-text-field
          ref="inputRef"
          variant="outlined"
          :placeholder="$t('новый адрес')"
          :error-messages="$t(errorMessage)"
          :hide-details="!error"
          autofocus
          :prefix="`${host}/`"
          density="compact"
          rounded="lg"
          bg-color="#fff"
          @input="name = name.replace(/\s/g, '').toLowerCase()"
          v-model="name"
          class="name-field"
          @update:model-value="checkAlias"
          clearable
          maxlength="30"
      >
        <template v-slot:clear>
          <v-hover v-if="!nameLoading">
            <template v-slot:default="{ props }">
              <v-icon :icon="ClearIcon" v-bind="props" @click="name = '';clearError()" v-if="!!variants.length || error"/>
            </template>
          </v-hover>
        </template>
        <template v-slot:append-inner>
          <v-fade-transition leave-absolute>
            <v-icon :icon="SuccessIcon" size="20" v-if="name && !variants.length && !nameLoading && !error"/>
            <v-progress-circular v-else-if="nameLoading" size="24" :width="2" indeterminate color="primary" bg-color="transparent"/>
          </v-fade-transition>
        </template>
        <template #message>
          <div class="error-text">
            <template v-if="errorCode != 403">
              {{ $t(errorMessage) }}
            </template>
            <i18n-t keypath="В адресе есть недопустимые слова. Чтобы использовать этот адрес, свяжитесь со " v-else>
              <template #support>
                <span @click="openRocket" class="link">
                  {{ $t('службой поддержки') }}
                </span>
              </template>
            </i18n-t>
          </div>
        </template>
        <template #details>
          <v-menu v-model="showMenu" v-if="!!variants.length" :target="inputRef">
            <div class="variants">
              <div v-for="variant in variants" class="variant" @click="updateName(variant)">
                <div>{{variant}}</div>
                <v-icon :icon="SuccessIcon" size="20"/>
              </div>
            </div>
          </v-menu>
        </template>
      </v-text-field>

      <v-btn variant="flat" color="primary" rounded="lg" :disabled="progress" class="create-button"
             @click="checkAuthAndCreatePage()">
        <template v-if="progress">
          <v-progress-circular indeterminate size="18" :width="2" color="white" bg-color="transparent" class="mr-2"/>
          {{ $t('Меняем адрес...') }}
        </template>
        <template v-else>
          {{$t('Поменять адрес')}}
        </template>
      </v-btn>
    </div>
  </div>
  <div class="text-center position-relative mb-5" v-else>
    <transition name="fade-copy">
      <div v-if="showCopy" class="green-copy"
           @touchstart="touchStart"
           @touchmove="touchMove"
           @touchend="touchEnd"
           :style="{ transform: `translate(${offsetX}px, ${offsetY}px)` }">
        {{ $t('Скопировано') }}
      </div>
    </transition>
    <v-icon :icon="SuccessIcon" size="40"/>
    <div class="title mt-2 mb-2">
      {{$t('Готово!')}}
    </div>
    <div class="d-flex justify-center text-center mb-4">
      <div class="delete-description">
        {{$t('Адрес вашей страницы успешно изменён')}}
      </div>
    </div>
    <div>
      <v-btn variant="flat" color="secondary" @click="copy()" rounded="lg" class="w-100">
        {{$t('Скопировать ссылку')}}
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">

import {onMounted, ref} from "vue";
import {useDialogStore} from "@/stores/dialogStore";
import {useTapStore} from '@/stores/tapStore';
import ClearIcon from "~/assets/icons/clear.vue";
import SuccessIcon from "~/assets/icons/success.vue";
import {useRoute, useRouter} from "vue-router";

const config = useRuntimeConfig()
const host = config.public.tapLink;

const props = defineProps({
  id: { type: Number },
  name: { name: String, default: '' },
  new: { name: Boolean, default: true }
})

const errorMessage = ref('')

const tapStore = useTapStore();

const dialogStore = useDialogStore()
const showCopy = ref(false)

let startX = 0
let startY = 0
const offsetX = ref(0)
const offsetY = ref(0)

const step = ref(1)
const name = ref('')
const nameLoading = ref(false)
const variants = ref<string[]>([])
const error = ref(false)
const showMenu = ref(false)
const errorCode = ref(0)
const inputRef = ref(null)
const progress = ref(false)

const openRocket = () => {
  //@ts-nocheck
  RocketChat(function() {
    this.showWidget();
    this.maximizeWidget();
  });
}

function touchStart(e: TouchEvent) {
  const t = e.touches[0]
  startX = t.clientX
  startY = t.clientY
}

function touchMove(e: TouchEvent) {
  const t = e.touches[0]
  offsetX.value = t.clientX - startX
  offsetY.value = t.clientY - startY
}

function touchEnd() {
  if (Math.abs(offsetX.value) > 30 || Math.abs(offsetY.value) > 30) {
    showCopy.value = false
  }

  offsetX.value = 0
  offsetY.value = 0
}

const checkAlias = () => {
  if(!name.value) {
    clearError()
    return
  }

  nameLoading.value = true
  tapStore.checkAlias(name.value.replace(/\s/g, '').toLowerCase()).then(result => {
    variants.value = result;
    error.value = result.length > 0
    showMenu.value = error.value
    errorMessage.value = error.value ? 'Такой адрес уже используется' : ''
  }).catch(e => {
    variants.value = []
    error.value = true
    if(e.status == 400) {
      errorMessage.value = 'Ссылка может содержать от 3 до 30 символов и состоять из латинских букв, цифр и символов «.»и «_»'
      errorCode.value = e.status
    }
    if(e.status == 403) {
      errorMessage.value = 'error'
      errorCode.value = e.status
    }
  }).finally(() => {
    nameLoading.value = false
  })
}

const clearError = () => {
  error.value = false
  errorMessage.value = ''
  errorCode.value = 0
  variants.value = []
}

const checkAuthAndCreatePage = () => {
  if(error.value) {
    return
  }
  changePage()
}

const route = useRoute()
const router = useRouter()

const changePage = () => {
  if(!name.value) {
    clearError()
    variants.value = []
    error.value = true
    errorMessage.value = 'Ссылка может содержать от 3 до 30 символов и состоять из латинских букв, цифр и символов «.»и «_»'
    errorCode.value = 400
    return
  }

  const newName = name.value.replace(/\s/g, '').toLowerCase()

  progress.value = true
  tapStore.changeTap(name.value.replace(/\s/g, '').toLowerCase(), props.id).then(() => {
    step.value = 2;
    if (route.path.includes('/edit')) {
      router.replace(`/profile/${newName}/edit`)//чтобы в адресной строке обновилось
      dialogStore.setSkipHistoryProp(true)//чтобы при закрытии диалога осталось тем же
    } else {
      tapStore.getTapList()
    }
  })
}

function copy() {
  navigator.clipboard.writeText(`${host}/${name.value.replace(/\s/g, '').toLowerCase()}`)
  showCopy.value = true

  setTimeout(() => {
    showCopy.value = false
  }, 1500)
}

const updateName = (variant: any) => {
  name.value = variant.replace(/\s/g, '').toLowerCase()
  checkAlias()
}
onMounted(() => {
  if(props.name) {
    name.value = props.name.replace(/\s/g, '').toLowerCase()
    changePage()
  }
  inputRef.value?.focus()
})

</script>

<style scoped lang="sass">
.title
  color: #363636
  text-align: center
  font-size: 20px
  font-weight: 600
  line-height: normal
.copy-description
  color: #363636
  text-align: center
  font-size: 14px
  font-weight: 400
  line-height: normal
  max-width: 280px
.name-field
  ::v-deep
    .v-input__control
      .v-text-field__prefix
        color: #363636
        opacity: 1
        font-size: 14px
        font-weight: 400
        line-height: 20px

      .v-field__input
        padding-inline: 0 var(--v-field-padding-end)
.variants
  border-radius: 8px
  border: 1px solid #CED4DA
  padding: 4px 0
  background: #FFF
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.12)
  .variant
    padding: 10px 16px
    gap: 8px
    display: flex
    justify-content: space-between
    color: #363636
    font-size: 14px
    line-height: 20px
    &:hover
      background: #F0F1F5
      cursor: pointer
.bg-secondary
  border: 1px solid #CED4DA
.green-copy
  position: absolute
  top: 30px
  left: 0
  background: #05A660D9
  width: 100%
  padding: 8px 16px
  border-radius: 8px
  color: #fff
  font-weight: 500
</style>
